import React, {
  useState,
  useEffect,
  useContext,
  createRef,
  ReactNode,
} from "react";

import { inject, observer } from "mobx-react";
import {
  IComponentCommentList,
  IComponent,
  IComponentCommentSend,
  IComponentVote,
} from "../../component.d";
import { ThemeContext } from "../../store/ThemeProvider";
import intl from "react-intl-universal";
import { alpha, getPendantColor } from "../../utils/color";
import { style } from "../../utils/image";
import {
  getHumanTime,
  getComponentById,
  getFileExt,
  formatSize,
  getPageSameType,
  saveFile,
  isIos
} from "../../utils/util";
import moment from "moment";
import SimpleBigPic from "../BigPic/SimpleBigPic";
import { ToMiniProgramBtn } from '../ComponentNavigator/JigsawBottom';
import { IComment, ICommentType, ICommentListTheme } from "./AllForCommentList";
import RootStore from "../../store/RootStore";

interface ICommentItemRender {
  data: IComment;
  onRecomment: (data: IComment) => void;
  children?: ReactNode;
  addonAfter?: ReactNode;
  onClick?: (source: IComment, extra: any) => void;
  rootStore?: RootStore;
}

const FILE_ICON = {
  ppt: require("../../assets/file-ppt.png"),
  word: require("../../assets/file-word.png"),
  pdf: require("../../assets/file-pdf.png"),
  excel: require("../../assets/file-excel.png"),
} as {
  [key: string]: string;
};

let voteDataMap = {} as {
  [key:string]:boolean;
};
const hasVote = (voteId:string | number)=>{
  if(typeof voteDataMap[String(voteId)] != 'undefined') {
    return voteDataMap[String(voteId)];
  }else {
    let votes = getPageSameType(window.pageDetail.template, [], 'vote') as IComponent[];
    let has = false;

    if(votes && votes.length) {
      for(var i=0;i<votes.length;i++) {
        let one = votes[i] as IComponentVote;
        if(one && String(one.vote_id) == String(voteId)) {
          has = true;
          break;
        }
      }
    }

    voteDataMap[String(voteId)] = has;
    return has;
  }
};

const ReplyList = (props: { list?: IComment[] }) => {
  const { list } = props;
  const { mainColor, h1Color, componentColor } = useContext(ThemeContext);

  const [more, setMore] = useState(false);

  return list && list.length ? (
    <div className="reply-list" style={{ background: alpha(h1Color, 0.05) }}>
      {list
        .map((it: IComment, index: number) => {
          if (!more && index >= 3) {
            return null;
          }
          return (
            <div
              className="reply-list-item"
              key={it.id}
              style={{ color: alpha(h1Color, 0.4) }}
            >
              {it.nick_name} {intl.get("Recomment")}：{it.content}
            </div>
          );
        })
        .filter(Boolean)}
      {!more && list.length > 3 ? (
        <div
          className="reply-list-more"
          onClick={() => {
            setMore(true);
          }}
          style={{ color: mainColor }}
        >
          {intl.get("MoreRecomment", {
            total: list.length,
          })}
        </div>
      ) : null}
    </div>
  ) : (
    <div />
  );
};

const ListItemNormal = (props: ICommentItemRender) => {
  const { data, onRecomment } = props;
  const {
    avatar,
    nick_name,
    is_master,
    is_top,
    master_name,
    created_at,
    has_reply,
    replylist,
    type,
  } = data;

  const { mainColor, h1Color, componentColor } = useContext(ThemeContext);
  const [bgColor, foreColor] = getPendantColor(mainColor);

  return (
    <div className="normal">
      <div className="avatar">
        <img
          src={
            avatar && avatar.indexOf("//") > -1
              ? avatar
              : style(avatar, "avatar_150")
          }
        />
      </div>
      <div className="wrapper">
        <div className="nickname" style={{ color: alpha(h1Color, 0.4) }}>
          {nick_name}
          {is_master ? (
            <div
              className="tag-master"
              style={{ color: foreColor, background: mainColor }}
            >
              {master_name || intl.get("organizer")}
            </div>
          ) : null}
          {is_top ? (
            <div
              className="tag-top"
              style={{ color: foreColor, background: mainColor }}
            >
              {intl.get("Top")}
            </div>
          ) : null}
        </div>
        <div className="content">
          {props.children}
          <div className="operation">
            <div className="time" style={{ color: alpha(h1Color, 0.4) }}>
              {getHumanTime(moment(created_at).valueOf())}
            </div>
            <div className="recomment">
              {is_master || is_top || type == ICommentType.QUESTION ? null : (
                <div className="tag-recomment" style={{ color: h1Color }}>
                  {intl.get("Recomment")}
                  <input
                    onClick={() => {
                      onRecomment(data);
                    }}
                    readOnly={true}
                    value=""
                  />
                </div>
              )}
            </div>
          </div>
          {props.addonAfter}
        </div>
      </div>
    </div>
  );
};

export const ThemeListComment = (props: ICommentItemRender) => {
  const { data, onClick, rootStore } = props;
  const { type, has_reply, replylist } = data;

  const { mainColor, h1Color, componentColor } = useContext(ThemeContext);

  const renderComment = () => {
    if (type == ICommentType.TEXT) {
      return (
        <ListItemNormal
          {...props}
          addonAfter={has_reply ? <ReplyList list={replylist} /> : null}
        >
          <div className="type-text" style={{ color: h1Color }}>
            {data.content}
          </div>
        </ListItemNormal>
      );
    }
    if (type == ICommentType.HTML) {
      let content = data.content;
      try {
        content = decodeURIComponent(content);
      } catch (e) {
        content = "";
      }
      return (
        <ListItemNormal
          {...props}
          addonAfter={has_reply ? <ReplyList list={replylist} /> : null}
        >
          <div className="type-text" style={{ color: h1Color }}>
            <div
              dangerouslySetInnerHTML={{
                __html: content,
              }}
              className="html-rawdata"
            />
          </div>
        </ListItemNormal>
      );
    }
    if (type == ICommentType.QUESTION) {
      return (
        <ListItemNormal {...props}>
          <div
            className={`type-question ${has_reply ? "has_reply" : ""}`}
            style={has_reply ? { background: alpha(h1Color, 0.05) } : {}}
          >
            <div className="question" style={{ color: h1Color }}>
              <img
                className="icon-question"
                src={require("../../assets/icon-question.png")}
              />
              <span>{data.content}</span>
            </div>
            {has_reply ? (
              <div
                className="answer"
                style={{ borderTopColor: alpha(h1Color, 0.1) }}
              >
                {replylist &&
                  replylist.map((it: IComment) => {
                    return (
                      <div className="answer-item" key={it.id}>
                        <img
                          className="icon-answer"
                          src={require("../../assets/icon-answer.png")}
                        />
                        <span style={{ color: alpha(h1Color, 0.4) }}>
                          {it.nick_name}：{it.content}
                        </span>
                      </div>
                    );
                  })}
              </div>
            ) : null}
          </div>
        </ListItemNormal>
      );
    }
    if (type == ICommentType.IMAGE) {
      return (
        <ListItemNormal
          {...props}
          addonAfter={has_reply ? <ReplyList list={replylist} /> : null}
        >
          <div
            className="type-image"
            style={{ color: h1Color }}
            onClick={() => {
              onClick && onClick(data, data.content);
            }}
          >
            <img src={style(data.content, "wx_375_240")} />
          </div>
        </ListItemNormal>
      );
    }
    if (type == ICommentType.FILE) {
      try {
        let fileData = JSON.parse(data.content);
        let ext = getFileExt(fileData.file);

        const useMiniapp = 0 && window.isweixin && !window.isweixinweb;

        return (
          <ListItemNormal {...props}>
            <div
              className="type-file"
              style={{ color: h1Color }}
              onClick={() => {
                // 如果当前是微信H5环境，则启用小程序渲染，当前则不处理
                if(!useMiniapp) {
                  saveFile(`//image1${window.__basename}/${fileData.file}`, fileData.name);
                }
              }}
            >
              <div className="file-preview">
                <img src={FILE_ICON[ext]} />
                <div className="file-preview-name">{fileData.name}</div>
                {useMiniapp ? 
                  <ToMiniProgramBtn style={{width:`100%`, height:`100%`, position:`absolute`}} downloadUrl={`//image1${window.__basename}/${fileData.file}`} label={'下载文件'} color={' '} />
                : null}
              </div>
              <div className="file-info" style={{ color: alpha(h1Color, 0.4) }}>
                {formatSize(Number(fileData.size))}
              </div>
            </div>
          </ListItemNormal>
        );
      } catch (e) {}
    }
    if (type == ICommentType.GIFT) {
      try {
        let giftData = JSON.parse(data.content);
        return (
          // <ListItemNormal
          //   {...props}
          //   addonAfter={has_reply ? <ReplyList list={replylist} /> : null}
          // >
          //   <div className="type-gift" style={{ color: h1Color }}>
          //     <img src={style(giftData.image, 'avatar_150')} />
          //   </div>
          // </ListItemNormal>
          <div className="message type-gift">
            <div
              className="border"
              style={{ background: alpha(h1Color, 0.05), color: h1Color }}
            >
              <span>
                {data.nick_name} {intl.get("Given")}
              </span>
              <img src={style(giftData.image, "avatar_150")} />{" "}
              <span>X {giftData.count}</span>
            </div>
          </div>
        );
      } catch (e) {}
    }
    if (type == ICommentType.RED_PACKAGE) {
      try {
        let redData = JSON.parse(data.content);
        const gotList = rootStore!.redPackStore.gotRedPackList();
        let hasGot = false;
        if (gotList && redData && gotList.indexOf(String(redData.id)) > -1) {
          hasGot = true;
        }
        return (
          <ListItemNormal {...props}>
            <div
              className="red-pack-content type-redpackage"
              onClick={() => {
                onClick && onClick(data, redData);
              }}
            >
              <div className={"main-pack-content " + (hasGot ? "goted" : "")}>
                <div className="content-left">
                  <img
                    src={require("../../assets/redpack/red-pack-normal.png")}
                    className={"red-pack-normal"}
                    alt=""
                  />
                </div>
                <div className="content-right">
                  <div className="real-content">{redData.content}</div>
                  {/*}<div>{intl.get('Open Red Packet')}</div>*/}
                </div>
              </div>
              <div className="footer">
                <div className="fz10">{intl.get("Red Packet")}</div>
              </div>
            </div>
          </ListItemNormal>
        );
      } catch (e) {
        console.error("解析红包数据错误", data.content);
      }
    }
    if (type == ICommentType.EMOTION) {
      try {
        let emotionData = JSON.parse(data.content);
        return (
          <ListItemNormal
            {...props}
            addonAfter={has_reply ? <ReplyList list={replylist} /> : null}
          >
            <div className="type-gift" style={{ color: h1Color }}>
              <img src={style(emotionData.image, "avatar_150")} />
            </div>
          </ListItemNormal>
        );
      } catch (e) {}
    }
    if (type == ICommentType.VOTE) {
      try {
        let voteData = JSON.parse(data.content);
        // TODO 此处暂时先隐藏掉，以后考虑当检测不到组件时，则生成一个组件
        if(voteData && voteData.vote_id) {
          const hasComponent = hasVote(voteData.vote_id);
          if(!hasComponent) {
            return null;
          }
        }
        return (
          <div className="comment-item">
            <div className="normal">
              <div className="type-vote">
                <div
                  className="border"
                  style={{ background: alpha(h1Color, 0.05), color: h1Color }}
                >
                  <p>主办方发起了</p>
                  <p>
                    “{voteData.question_title}”的投票，
                    <a
                      style={{ color: mainColor }}
                      onClick={() => {
                        onClick && onClick(data, voteData);
                      }}
                    >
                      点击参与
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      } catch (e) {}
    }
    console.log("nothing to render comment", type);
  };

  return <div className="comment-item">{renderComment()}</div>;
};

// 先从界面上查找出评论发送组件，如果这个组件没有开启提问，则不要显示提问过滤标签 - 202007-31
const commentsend = getPageSameType(
  window.pageDetail.template,
  [],
  ["commentsend"]
);
let showQuestion = true;
if (commentsend && commentsend.length) {
  let target = commentsend[0] as IComponentCommentSend;
  if (target && target.question === false) {
    showQuestion = false;
  }
}

const ThemeList = (props: ICommentListTheme) => {
  const {
    rootStore,
    messages,
    total,
    onRecomment,
    onSortChange,
    onFetchList,
    headerExtra,
  } = props;

  const { componentColor, h1Color, mainColor } = useContext(ThemeContext);
  const [bgColor, foreColor] = getPendantColor(mainColor);
  const { loadedMessagesLength, finalComment } = rootStore.commentV2Store!;
  const [active, setActive] = useState("last");
  const filter = [
    { key: "last", label: intl.get("Lastest") },
    { key: "early", label: intl.get("Earliest") },
    showQuestion ? { key: "question", label: intl.get("Question") } : null,
    { key: "text", label: intl.get("OnlyComment") },
  ].filter(Boolean);

  const commentlist = createRef<HTMLDivElement>();
  const fixedListHeight = (dom: HTMLDivElement) => {
    let maxHeight = window.innerHeight - dom.offsetTop;
    // 还要查找当前组件
    let component = getComponentById(window.pageDetail.template, props.cid);
    if (component && component.container) {
      let marginBottom = Number(component.container.bottom) || 15;
      if (marginBottom) {
        maxHeight -= marginBottom;
      }
    }
    dom.style.height = `${maxHeight - 66}px`;
  };

  useEffect(() => {
    // 暂时去掉，因为整个界面，其实要跟着滑动，那么评论区域，理论上是需要全部显示的
    // let dom = commentlist.current!;
    // fixedListHeight(dom);
    //
    // const bindFixed = ()=>{
    //   fixedListHeight(dom);
    // };
    //
    // const tempTimer = setTimeout(bindFixed, 500);
    //
    // window.addEventListener('resize', bindFixed);
    // return ()=>{
    //   clearTimeout(tempTimer);
    //   window.removeEventListener('resize', bindFixed);
    // }
  }, []);

  // 每次render结束，都要执行一下tab的高度更新
  useEffect(() => {
    window.dispatchEvent(new Event("custom-tab-height-change"));
  });

  const setCurrentRedpack = (data: any = {}, source: IComment) => {
    const { redPackStore, wxuserStore } = props.rootStore;

    if (wxuserStore.user.is_mock) {
      wxuserStore.auth();
      return;
    }
    // console.log(data, source);
    redPackStore.setRedPackList([
      {
        id: data.id,
        content: data.content,
        postname: source.nick_name,
        postphoto: source.avatar,
      },
    ]);
  };

  // 大图相关的内容
  const [bigIndex, setBigIndex] = useState(-1 as number);
  const [bigList, setBigList] = useState([] as any[]);

  const reformatBigList = () => {
    let commentImageList = messages
      .map((it: IComment, index: number) => {
        if (it && it.type == ICommentType.IMAGE) {
          // style(data.content, 'wx_375_240')
          return {
            id: it.id,
            hash: it.content,
          };
        } else {
          return null;
        }
      })
      .filter(Boolean);

    setBigList(commentImageList);
    return commentImageList;
  };

  // useEffect(()=>{
  //   if(bigIndex > -1) {
  //     reformatBigList();
  //   }
  // }, [bigIndex > -1]);

  const onCommentItemClick = (data: IComment, extra: any) => {
    if (data && data.type == ICommentType.RED_PACKAGE) {
      setCurrentRedpack(extra, data);
    }

    if (data && data.type == ICommentType.IMAGE) {
      let commentImageList = reformatBigList();
      let targetIndex = commentImageList.findIndex((it: any) => {
        if (it && it.id == data.id) {
          return true;
        }
        return false;
      });
      setBigList(commentImageList);
      setBigIndex(targetIndex);
    }

    if (data && data.type == ICommentType.VOTE) {
      // if (rootStore!.wxuserStore.user.is_mock) {
      //   rootStore!.wxuserStore.auth();
      //   return;
      // }
      let event = new Event("vote_popup");
      event.data = (extra || {}).vote_id;
      window.dispatchEvent(event);
    }
  };

  return (
    <div
      ref={commentlist}
      className="commentlist theme-list"
      style={{ background: componentColor }}
    >
      <div className="header" style={{ background: alpha(componentColor, 1) }}>
        <div className="title" style={{ color: h1Color, flex: 1 }}>
          {intl.get("Comment")} {total}
        </div>
        <div className="filter" style={{ background: alpha(h1Color, 0.05) }}>
          {filter.map((it: any) => {
            return (
              <div
                key={it.key}
                onClick={() => {
                  setActive(it.key);
                  if (onSortChange) onSortChange(it.key);
                }}
                className={`filter-item ${it.key == active ? "active" : ""}`}
                style={
                  it.key == active
                    ? {
                        background: mainColor,
                        color: foreColor,
                      }
                    : {
                        color: alpha(h1Color, 0.4),
                      }
                }
              >
                {it.label}
              </div>
            );
          })}
        </div>
        {headerExtra}
      </div>
      <div className="list">
        {total ? (
          messages.map((it: IComment, index: number) => {
            return (
              <ThemeListComment
                data={it}
                onRecomment={onRecomment}
                key={`${index}${it.id}`}
                // onClick={setCurrentRedpack}
                onClick={onCommentItemClick}
                rootStore={rootStore}
              />
            );
          })
        ) : (
          <div className="list-empty" style={{ height: 156 }}>
            <img
              src={require("../../assets/commentv2-empty.png")}
              style={{ height: "auto" }}
            />
            <p
              style={{
                color: alpha(h1Color, 0.4),
                fontSize: 12,
                textAlign: "center",
                marginTop: 5,
              }}
            >
              {intl.get("No comments")}
            </p>
          </div>
        )}
        {/*如果存在数据，并且当前数据条数与total不一致，则显示更多按钮*/}
        {total ? (
          loadedMessagesLength < total && !finalComment ? (
            <div
              className="loadmore"
              style={{ color: alpha(h1Color, 0.4) }}
              onClick={() => {
                if (onFetchList) onFetchList(active, true);
              }}
            >
              {intl.get("Load more")}
            </div>
          ) : (
            <div className="loadmore" style={{ color: alpha(h1Color, 0.4) }}>
              {intl.get("This is the end")}
            </div>
          )
        ) : null}
      </div>

      <SimpleBigPic
        pageType="CommentList"
        index={bigIndex}
        list={bigList}
        setIndex={setBigIndex}
      />
    </div>
  );
};

export default ThemeList;
